import { DashboardSquare01Icon, LinkForwardIcon, Refresh01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { createFileRoute } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

import { DASHBOARDS } from '@/lib/dashboards';
import { getDashboardUrl, useDashboardThemeSync } from '@/lib/dashboard-theme';

export const Route = createFileRoute('/_authenticated/reports/dashboards')({
  component: DashboardsPage,
});

function DashboardsPage() {
  const { t } = useTranslation();
  const [activeDashboard, setActiveDashboard] = useState<any | null>(null);
  const [iframeKey, setIframeKey] = useState(0);
  const [search, setSearch] = useState('');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useDashboardThemeSync(iframeRef);

  const filteredDashboards = DASHBOARDS.filter(
    (d) =>
      d.title.toLowerCase().includes(search.toLowerCase()) ||
      d.description.toLowerCase().includes(search.toLowerCase()) ||
      d.category.toLowerCase().includes(search.toLowerCase()),
  );

  const categories = [...new Set(filteredDashboards.map((d) => d.category))];

  if (activeDashboard) {
    const iframeUrl = getDashboardUrl({ dashboard: activeDashboard.id, showSidebar: 'false' });

    return (
      <div className="space-y-4 h-[calc(100vh-2rem)] flex flex-col">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setActiveDashboard(null)}>
              ← Back
            </Button>
            <div>
              <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
                <HugeiconsIcon icon={DashboardSquare01Icon} className="size-5" />
                {activeDashboard.title}
              </h1>
              <p className="text-muted-foreground text-sm">{activeDashboard.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setIframeKey((k) => k + 1)}>
              <HugeiconsIcon icon={Refresh01Icon} className="size-4 mr-1" />
              {t('buttons.refresh')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.open(iframeUrl, '_blank')}>
              <HugeiconsIcon icon={LinkForwardIcon} className="size-4 mr-1" />
              {t('buttons.openInNewTab')}
            </Button>
          </div>
        </div>

        <Card className="flex-1 overflow-hidden min-h-0">
          <CardContent className="p-0 h-full">
            <iframe
              key={iframeKey}
              ref={iframeRef}
              src={iframeUrl}
              className="w-full h-full border-0"
              title={activeDashboard.title}
              allow="fullscreen"
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <HugeiconsIcon icon={DashboardSquare01Icon} className="size-6" />
            {t('sidebar.reports.dashboards', 'Dashboards')}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t(
              'reports.dashboards.description',
              'Interactive dashboards for operations, maintenance, and reliability metrics',
            )}
          </p>
        </div>
        <Input
          placeholder="Search dashboards…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
      </div>

      {categories.map((category) => (
        <div key={category}>
          <h2 className="text-lg font-semibold mb-3 text-muted-foreground">{category}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredDashboards
              .filter((d) => d.category === category)
              .map((dashboard) => (
                <Card
                  key={dashboard.id}
                  className="cursor-pointer hover:border-primary/50 hover:shadow-md transition-all"
                  onClick={() => setActiveDashboard(dashboard)}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{dashboard.title}</CardTitle>
                    <CardDescription className="text-xs">{dashboard.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                        {dashboard.category}
                      </span>
                      <span className="text-xs text-primary font-medium">Open →</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}
